import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/features/shared/components/ui/button';
import { InstallPanel } from './InstallPanel';
import { useInstallPrompt } from './useInstallPrompt';
import { shouldShowInstallCard, readDismissedAt, noteDismissed } from './installDismissal';

/**
 * The install offer on the home dashboard — the one place most people will
 * actually see it.
 *
 * It is the existing InstallPanel with a "Maybe later" that is remembered for
 * 30 days, nothing more: one panel, one set of copy, one set of browser
 * instructions. Account settings keeps the permanent path, so dismissing this
 * never removes the ability to install.
 *
 * Renders nothing when the app is already running installed — InstallPanel
 * decides that, since "installed" is a property of this window, not of the
 * dashboard.
 */
export const InstallCard: React.FC = () => {
  const { t } = useTranslation('common');
  const { mode } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(
    () => !shouldShowInstallCard(Date.now(), readDismissedAt()),
  );

  // ⚠️ Checked here as well as inside the panel: without it the "Maybe
  // later" row would render under an empty panel on a browser that has no
  // install route, which looks broken.
  if (mode === 'installed' || mode === 'none') return null;
  if (dismissed) return null;

  const later = () => {
    noteDismissed(Date.now());
    setDismissed(true);
  };

  return (
    <div data-testid="install-card" className="space-y-2">
      <InstallPanel />
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" onClick={later} data-testid="install-card-later">
          {t('install.later', 'Maybe later')}
        </Button>
      </div>
    </div>
  );
};
